import React, { useEffect, useState, useContext, useReducer } from 'react'
import { useParams, Link, useHistory } from 'react-router-dom'
import { isAfter, parseISO } from 'date-fns'
import cn from 'classnames'
import useSocket from 'use-socket.io-client'

import { ModalContext } from 'modaaals'
import SecureButton from './SecureButton'
import Form from './Form'
import { authRequest } from './api'
import styles from './request.module.css'

export const reducer = (state, action) => {
  switch (action.type) {
    case 'RESET':
      return {
        sending: false,
        sent: false,
        error: null,
      }
    case 'SENDING':
      return {
        sending: true,
        sent: false,
        error: null,
      }
    case 'SENT':
      return {
        sending: false,
        sent: true,
        error: null,
      }
    case 'ERROR':
      return {
        sending: false,
        sent: false,
        error: action.error,
      }
    default:
      throw new Error()
  }
}

export default function RequestDetail({ requestType }) {
  let { id: requestId, userId } = useParams()

  let history = useHistory()

  const [name, setName] = useState('')
  const [info, setInfo] = useState(null)

  const [state, dispatch] = useReducer(reducer, {
    sending: false,
    sent: false,
    error: false,
  })

  const { openModal } = useContext(ModalContext)
  const [socket] = useSocket(`/`, {
    autoConnect: false,
  })

  useEffect(() => {
    if (history.location.search) {
      const searchParams = new URLSearchParams(history.location.search)
      const queryParamsObj = Object.fromEntries(searchParams.entries())
      if (queryParamsObj.openChat) {
        setTimeout(
          () => openModal({ type: 'chat', id: requestId, userId }),
          1000
        )
      }
    }
  }, [history.location.search, openModal, requestId, userId])

  useEffect(() => {
    function fetchInfo() {
      return authRequest(
        `/admin/request?userId=${userId}&requestType=${requestType}&requestId=${requestId}`
      )
        .then(({ item, user }) => {
          if (item) {
            setName(user.name)
            setInfo(item)
          } else {
            history.replace(`/user/${userId}`)
          }
        })
        .catch((err) => {
          history.replace(`/user/${userId}`)
        })
    }

    if (requestType && requestId) {
      fetchInfo()
    }

    socket.connect()
    socket.on('connect', function () {
      socket.emit('room', userId)
    })
    socket.on('chat-msg-from-client', fetchInfo)
    socket.on('updated-value-from-client', fetchInfo)
    socket.on('message-read', fetchInfo)
    return () => socket.disconnect()
  }, [requestType, requestId, userId, socket, history])

  function onSubmit(values) {
    authRequest(`/admin/request`, {
      method: 'POST',
      body: JSON.stringify({
        userId,
        requestType,
        ...values,
      }),
    })
      .then(setInfo)
      .catch((err) => console.log({ err }))
  }

  const newMessages = info
    ? hasNewMessages(info.messagesOpenedOn, info.messages)
    : null

  return info ? (
    <section className="wrapper">
      <div className="topbar">
        <h1 style={{ textTransform: 'capitalize' }}>
          {name} {requestType} Item
        </h1>
      </div>
      <div className="topbar">
        <Link to={`/user/${userId}`}>&lt; Back to {name} User page</Link>{' '}
        {requestType === 'purchased' && (
          <Link
            style={{ marginLeft: '1em' }}
            to={`/user/${userId}/${info.refRequestType}/${info.refRequestId}`}
          >
            Back to Reference
          </Link>
        )}
        <SecureButton
          confirm={`Are you sure ????`}
          confirmClassName="deleteBtn deleteBtnConfirm"
          className="deleteBtn"
          style={{ marginRight: '2em' }}
          type="button"
          onConfirm={async () => {
            if (
              window.confirm(
                `Last call: are your pretty sure to Delete this ${requestType}?`
              )
            ) {
              authRequest(`/admin/request`, {
                method: 'DELETE',
                body: JSON.stringify({
                  userId,
                  requestType,
                  requestId,
                }),
              })
                .then(() => {
                  history.replace(`/user/${userId}`)
                })
                .catch((err) => console.log({ err }))
            }
          }}
        >
          Delete {requestType}
        </SecureButton>
        {requestType !== 'purchased' && (
          <button
            className="btn btn-info btn-sm"
            type="button"
            style={{ position: 'relative' }}
            onClick={(e) => {
              e.preventDefault()
              openModal({ type: 'chat', id: requestId, userId })
            }}
          >
            Conversation{' '}
            {newMessages > 0 && (
              <span className={styles.badge}>{newMessages}</span>
            )}
          </button>
        )}
      </div>
      <div className="json-form">
        <Form
          initialValues={info}
          requestType={requestType}
          onSubmit={onSubmit}
          userId={userId}
        />

        <br />

        <SecureButton
          type="button"
          disabled={state.sent}
          confirm={`Sure send Notification Email?`}
          className={cn(styles.button, styles.orange)}
          confirmClassName={cn(styles.button, styles.red)}
          onConfirm={async () => {
            if (
              window.confirm(
                `Last call: You are sending an email with the content of the "to-user-${requestType}-notification" template?`
              )
            ) {
              dispatch({ type: 'SENDING' })
              authRequest(`/admin/email-notification`, {
                method: 'POST',
                body: JSON.stringify({
                  id: userId,
                  requestId,
                  requestType,
                }),
              })
                .then(() => dispatch({ type: 'SENT' }))
                .catch((err) => dispatch({ type: 'ERROR', error: err.message }))
            }
          }}
        >
          {state.sending
            ? 'Sending Notification Message…'
            : 'Send Notification Message'}
        </SecureButton>
        {state.sent && 'Message sent successfully'}
      </div>
    </section>
  ) : null
}

export function hasNewMessages(lastOpen, messages = []) {
  return messages.length > 0
    ? lastOpen
      ? messages.reduce((acc, message) => {
          return isAfter(parseISO(message.time), parseISO(lastOpen))
            ? acc + 1
            : acc
        }, 0)
      : messages.length
    : 0
}
